import { useState } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Document } from "@/types/documents";
import type { DocumentTemplate } from "../../constants/documentTemplates";
import type { TemplateFormValues } from "../../utils/templateForm";
import { useAttachableDocuments } from "../../hooks/useAttachableDocuments";
import { useTemplateFormMembers } from "../../hooks/useTemplateFormMembers";
import { useTemplateFormPrefill } from "../../hooks/useTemplateFormPrefill";
import { useTemplateFormSave, type SaveDestination } from "../../hooks/useTemplateFormSave";
import { useTemplateFormSources } from "../../hooks/useTemplateFormSources";
import { findCollidingDocument } from "../../services/templateFormSave";
import { NewDocumentModal } from "../NewDocumentModal";
import { useSigningAccess } from "@/domains/signing/useSigningAccess";
import { SignDocumentsDialog } from "../organisms/SignDocumentsDialog";
import { FolderPickerDialog } from "@/components/chat/SidebarChat/FolderPickerDialog";
import { TemplateFormDialog, type TemplateFormSubmission } from "./TemplateFormDialog";

interface NewDocumentFlowProps {
  /** Whether the template picker is open. The form view manages itself. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string;
  buildingName: string;
  /** Scopes the member picker; null when the building has no project yet. */
  projectId: string | null;
  /** The page's document list — used only when the building has no project
   *  to fetch from (it is server-filtered and paginated, so it never stands
   *  in for the project-scoped fetch). */
  documents: Document[];
  triggerRef: React.RefObject<HTMLButtonElement>;
  /**
   * Where a filed document goes. The folder view already knows — it is the
   * folder on screen — so it passes one. The Timeline and List views are not
   * scoped to a folder, so they pass `"ask"` and Save opens the folder picker
   * before filing anything.
   */
  saveDestination: "ask" | { folderId: string | null };
  /**
   * Set by a host that cannot vouch for its own `saveDestination` yet — the
   * folder view's `?folderId=` is a URL value until the folder list confirms
   * it. Blocks Save with this text, checked as the form is filed rather than
   * only when it was opened.
   */
  saveUnavailableReason?: string;
  /**
   * Reloads the page's document list once a form has been filed. The list is
   * an MST store, not a react-query cache, so invalidating query keys does not
   * bring the new document into view — the host has to reload it, exactly as
   * the upload path does through `onUploadComplete`.
   */
  onDocumentFiled: () => void;
}

/** The one project every picked-from-project attachment came from, if they
 *  agree on one. At building scope nothing else scopes the form, so the
 *  destination picker defaults to it — otherwise the user could pick
 *  attachments from project A and unknowingly file the form into project B. */
function attachmentsProjectId(values: TemplateFormValues | undefined): string | undefined {
  const ids = new Set<string>();
  for (const value of Object.values(values ?? {})) {
    if (!Array.isArray(value)) continue;
    for (const entry of value) {
      if (typeof entry === "object" && "source" in entry && entry.projectId) {
        ids.add(entry.projectId);
      }
    }
  }
  const [only] = ids;
  return ids.size === 1 ? only : undefined;
}

/**
 * "New document", end to end: pick a template, then fill its form. The two
 * views share this owner so the back arrow can hand control back to the
 * picker, and so the form gets the data it pre-fills from.
 */
export const NewDocumentFlow = observer(function NewDocumentFlow({
  open,
  onOpenChange,
  buildingId,
  buildingName,
  projectId,
  documents,
  triggerRef,
  saveDestination,
  saveUnavailableReason,
  onDocumentFiled,
}: NewDocumentFlowProps) {
  const { t } = useLanguage();
  const copy = t.documents.templateForm;
  const prefill = useTemplateFormPrefill(buildingId, buildingName);
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const members = useTemplateFormMembers(projectId, template !== null);
  const { documentNames } = useAttachableDocuments(
    projectId,
    documents,
    template !== null,
    copy.documentsError
  );
  // Which project's already-filed forms this form can start from or import a
  // field out of. Normally the one the page is scoped to; at building scope
  // there is none, so the picker asks and answers into this. Copying content
  // out of one project and filing into another is legitimate — the two
  // questions are genuinely separate, and only this one has an answer yet.
  const [sourceProjectId, setSourceProjectId] = useState<string | null>(null);
  const { savedForms, importSources } = useTemplateFormSources(
    projectId ?? sourceProjectId,
    template
  );

  const openTemplate = (chosen: DocumentTemplate | null) => {
    // A new template starts a clean form, so it starts a clean source too.
    setSourceProjectId(null);
    setTemplate(chosen);
  };

  // The same gate the documents bulk bar uses (DocumentsMainView) — one rule
  // for whether this org can sign at all, not a second one invented here. It
  // was a hand-copy of useSigningAccess's body despite that comment, which is
  // how the two drift; now that signing has TWO gates (self-sign released,
  // chained held back to platform admins) a stale copy is how a held-back
  // surface leaks. Call the hook.
  const signingEnabled = useSigningAccess();

  const { save, saving, toSign, clearToSign } = useTemplateFormSave({
    buildingId,
    organisation: prefill.organisation,
    onSaved: () => {
      openTemplate(null);
      onDocumentFiled();
    },
  });

  // Held while the folder picker is open: the form is filled and Save was
  // pressed, but there is nowhere to put it yet.
  const [awaitingDestination, setAwaitingDestination] = useState<TemplateFormSubmission | null>(
    null
  );

  // Held while the overwrite ask is open: the form is filled, the destination
  // is settled, and a document there already answers to this name.
  const [awaitingOverwrite, setAwaitingOverwrite] = useState<{
    submission: TemplateFormSubmission;
    destination: SaveDestination;
    existing: string;
  } | null>(null);

  // True from the moment Save is pressed until the save owns the spinner. The
  // collision check is a round-trip, and `saving` only flips once `save` runs —
  // so without this Save stays enabled across that gap and a second click files
  // the form twice, under two document numbers.
  const [checking, setChecking] = useState(false);

  /**
   * The one way this flow files a form, whichever route the destination came
   * from — so the collision check cannot be forgotten on one of them.
   */
  const fileInto = async (submission: TemplateFormSubmission, destination: SaveDestination) => {
    setChecking(true);
    try {
      // A name still following the number is rebuilt from the number the
      // database reserves, so the string that would be checked here is not the
      // string that gets filed. Only a typed name can collide by accident.
      if (!submission.nameFollowsNumber) {
        const existing = await findCollidingDocument({
          projectId: Number(destination.projectId),
          folderId: destination.folderId,
          filename: `${submission.documentName}.pdf`,
        });
        if (existing) {
          setAwaitingOverwrite({ submission, destination, existing: existing.filename });
          return;
        }
      }
      await save(submission, destination);
    } finally {
      setChecking(false);
    }
  };

  // The host's own reason first — it knows things this flow cannot, like
  // whether the folder it named is still one of the project's. Otherwise:
  // only when nothing can supply a project. Where Save asks for the
  // destination the picker provides one, so a page with no project scope
  // is not a reason to block — it is the reason to ask.
  const blockedReason =
    saveUnavailableReason ??
    (saveDestination !== "ask" && projectId === null ? copy.validation.noProject : undefined);

  const handleSave = (submission: TemplateFormSubmission) => {
    // Save is disabled while this holds, so reaching here means a submit got
    // past the button. Say why nothing was filed rather than swallowing it —
    // the reason is recoverable (retry the folder load, reopen the folder).
    // Checked before the picker opens, not after: a host that supplies a reason
    // *and* asks for a destination would otherwise file the form through a
    // button its own reason had disabled.
    if (blockedReason) {
      toast.error(blockedReason);
      return;
    }
    if (saveDestination === "ask") {
      setAwaitingDestination(submission);
      return;
    }
    if (projectId === null) {
      toast.error(copy.validation.noProject);
      return;
    }
    void fileInto(submission, {
      projectId,
      folderId: saveDestination.folderId ? Number(saveDestination.folderId) : null,
    });
  };

  return (
    <>
      <NewDocumentModal
        open={open}
        onOpenChange={onOpenChange}
        onSelectTemplate={openTemplate}
        triggerRef={triggerRef}
      />
      <TemplateFormDialog
        template={template}
        onOpenChange={(next) => openTemplate(next ? template : null)}
        onBack={() => {
          openTemplate(null);
          onOpenChange(true);
        }}
        prefill={prefill}
        existingDocumentNames={documentNames}
        buildingId={buildingId}
        projectId={projectId}
        members={members}
        savedForms={savedForms}
        // Only where the page cannot answer it. With a project in scope the
        // question is already settled, and asking again would invite drawing
        // an act's content out of a project it has nothing to do with.
        savedFormsProject={
          projectId === null
            ? { buildingId, projectId: sourceProjectId, onProjectChange: setSourceProjectId }
            : undefined
        }
        importSources={importSources}
        onSave={handleSave}
        // `checking` too, so Save is never live during the collision round-trip.
        saving={saving || checking}
        canSign={signingEnabled}
        // The same reason Save refuses on, so the disabled button and the
        // refusal can never disagree.
        saveUnavailableReason={blockedReason}
        triggerRef={triggerRef}
      />
      {awaitingDestination && (
        <FolderPickerDialog
          open
          onOpenChange={(next) => !next && setAwaitingDestination(null)}
          buildingId={buildingId}
          // When the page already scopes the form to a project, default the
          // destination to it, so what was picked from (attachments, members)
          // and where it files agree unless the user deliberately deviates.
          // At building scope no project scoped the page, but the attachment
          // picker may have scoped the user: default to the project their
          // attachments came from, for the same agreement.
          initialProjectId={projectId ?? attachmentsProjectId(awaitingDestination.values)}
          filename={`${awaitingDestination.documentName}.pdf`}
          title={copy.destination.title}
          description={copy.destination.description}
          submitLabel={copy.destination.submit}
          onSelect={(picked) => {
            const submission = awaitingDestination;
            setAwaitingDestination(null);
            void fileInto(submission, {
              projectId: picked.projectId,
              folderId: picked.folderId ? Number(picked.folderId) : null,
            });
          }}
        />
      )}
      {/* Sits above the still-open form dialog, so it needs the same
          pointer-events escape the discard ask does — a modal Radix dialog
          sets `pointer-events: none` on the body, and this portals there too. */}
      <AlertDialog
        open={awaitingOverwrite !== null}
        onOpenChange={(next) => !next && setAwaitingOverwrite(null)}
      >
        <AlertDialogContent className="pointer-events-auto z-[60]">
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.overwrite.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {copy.overwrite.description.replace(
                "{name}",
                () => awaitingOverwrite?.existing ?? ""
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* Cancel is the default action: versioning over someone else's
                document is not what a stray Enter should do. */}
            <AlertDialogCancel onClick={() => setAwaitingOverwrite(null)}>
              {copy.overwrite.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const pending = awaitingOverwrite;
                setAwaitingOverwrite(null);
                if (pending) void save(pending.submission, pending.destination);
              }}
            >
              {copy.overwrite.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {toSign && (
        <SignDocumentsDialog
          open
          onOpenChange={(next) => !next && clearToSign()}
          buildingId={Number(buildingId)}
          buildingName={buildingName}
          // The dialog files the signed container where THIS document is filed,
          // read from its own row — so the form no longer has to hand its
          // destination on, and cannot hand on one that disagrees.
          documents={[toSign.document]}
          onSigned={() => {
            clearToSign();
            // The container is a new document row, created only once signing
            // finalises — well after the save reloaded the list. Reload again
            // or it stays invisible until the user refreshes by hand.
            onDocumentFiled();
          }}
        />
      )}
    </>
  );
});
